import type {
  LockerverseCheckoutClient,
  LockerverseCheckoutEstimate,
  LockerverseCheckoutQuote,
  LockerversePaymentResult,
  LockerverseSubmitPaymentInput,
} from "@lockerverse/sdk/checkout";
import { LockerverseSdkError } from "@lockerverse/sdk/checkout";
import type { Stripe } from "@stripe/stripe-js";

const AMOUNT_KEYS = [
  "amountCents",
  "discountableSubtotalCents",
  "discountCents",
  "serviceFeeCents",
  "subtotalCents",
  "tipCents",
  "totalCents",
] as const satisfies readonly (keyof LockerverseCheckoutQuote)[];

const SERVER_CONTROLLED_AMOUNT_KEYS = [
  "discountableSubtotalCents",
  "discountCents",
  "subtotalCents",
] as const satisfies readonly (keyof LockerverseCheckoutQuote)[];

export function hasMaterialQuoteChange(
  displayed: LockerverseCheckoutEstimate | LockerverseCheckoutQuote,
  current: LockerverseCheckoutQuote,
  locallyPreviewedTotalCents: number | undefined,
  stripeConnectedAccountId: string | null
) {
  const amountKeys =
    locallyPreviewedTotalCents === current.totalCents
      ? SERVER_CONTROLLED_AMOUNT_KEYS
      : AMOUNT_KEYS;
  return (
    amountKeys.some((key) => displayed[key] !== current[key]) ||
    stripeConnectedAccountId !== current.payment.connectedAccountId ||
    (displayed.authoritative &&
      (displayed.payment.connectedAccountId !== stripeConnectedAccountId ||
        displayed.payment.currency !== current.payment.currency)) ||
    JSON.stringify(displayed.appliedDiscount) !==
      JSON.stringify(current.appliedDiscount) ||
    JSON.stringify(displayed.lineItems) !== JSON.stringify(current.lineItems)
  );
}

export type PaymentAttemptOutcome =
  | { kind: "failed"; payment: LockerversePaymentResult }
  | { kind: "success"; payment: LockerversePaymentResult }
  | {
      kind: "unresolved";
      paymentReference: string;
      reason: "action_required" | "pending" | "recovery_failed";
    };

function classifyPayment(
  payment: LockerversePaymentResult
): PaymentAttemptOutcome {
  if (payment.checkoutStatus === "success") {
    return { kind: "success", payment };
  }
  if (payment.checkoutStatus === "failed") {
    return { kind: "failed", payment };
  }
  return {
    kind: "unresolved",
    paymentReference: payment.paymentReference,
    reason:
      payment.checkoutStatus === "action_required"
        ? "action_required"
        : "pending",
  };
}

async function settleRequiredAction(
  client: LockerverseCheckoutClient,
  payment: LockerversePaymentResult,
  stripe: Stripe | null | undefined
): Promise<PaymentAttemptOutcome> {
  if (payment.checkoutStatus !== "action_required") {
    return classifyPayment(payment);
  }
  if (!stripe) {
    return {
      kind: "unresolved",
      paymentReference: payment.paymentReference,
      reason: "action_required",
    };
  }
  if (payment.clientSecret) {
    try {
      await stripe.handleNextAction({ clientSecret: payment.clientSecret });
    } catch {
      // Status recovery below is authoritative even when Stripe UI fails.
    }
  }
  try {
    const recovered = await client.getPaymentStatus(payment.paymentReference);
    return classifyPayment(recovered);
  } catch {
    return {
      kind: "unresolved",
      paymentReference: payment.paymentReference,
      reason: "action_required",
    };
  }
}

export async function submitPaymentAttempt(
  client: LockerverseCheckoutClient,
  input: LockerverseSubmitPaymentInput,
  stripe?: Stripe | null
): Promise<PaymentAttemptOutcome> {
  let payment: LockerversePaymentResult;
  try {
    payment = await client.submitPayment(input);
  } catch (error) {
    if (!(error instanceof LockerverseSdkError && error.recoveryRecommended)) {
      throw error;
    }
    try {
      payment = await client.getPaymentStatus(input.checkout.paymentReference);
    } catch {
      return {
        kind: "unresolved",
        paymentReference: input.checkout.paymentReference,
        reason: "recovery_failed",
      };
    }
  }
  return settleRequiredAction(client, payment, stripe);
}

export async function recoverPaymentAttempt(
  client: LockerverseCheckoutClient,
  paymentReference: string,
  stripe: Stripe | null
): Promise<PaymentAttemptOutcome> {
  try {
    const payment = await client.getPaymentStatus(paymentReference);
    return settleRequiredAction(client, payment, stripe);
  } catch {
    return {
      kind: "unresolved",
      paymentReference,
      reason: "recovery_failed",
    };
  }
}
